import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppStateProps } from "./types";
import { USD, USDSymbol } from "./constants";
import { getCurrencySymbol } from "@/uiCore";
import { GetTenantInfoResponse } from "@/helpers/types";
import { JIRA_TICKETING_ENABLED } from "@/config";
import { SMALL_SCREEN_BREAKPOINT } from "@/helpers/constants";

export const isSmallScreen = window.innerWidth < SMALL_SCREEN_BREAKPOINT;

export const initialState: AppStateProps = {
  currency: USD,
  currencySymbol: USDSymbol,
  teammatesEnabled: false,
  loggedinUser: null,
  hasAcceptedCookies: false,
  sidebarExpanded: !isSmallScreen,
  hideSidebarExpandIcon: false,
  authHeaderSet: false,
  previousPath: "",
  rbacEnabled: false,
  enableAutoTuneNotifications: false,
  userGroup: undefined,
  userGroupsLoaded: false,
  datastores: [],
  queryListingDefaultDaysCount: undefined,
  dbtWorkloadEnabled: false,
  jiraTicketEnabled: false,
  costAttributionEnabled: false,
  accessRulesDisabled: false,
  disableAccountCosts: false,
  costAnalysisReportsEnabled: false,
  companyInfo: null,
  streamlitNotebooksEnabled: false,
  enableStreamlitWorkloads: false,
  storedProceduresEnabled: false,
  sigmaWorkbooksEnabled: false,
  snowflakeTasksEnabled: false,
  enableCustomReports: false,
  enableCostReports: false,
  enableTenantSwitching: false,
  enableSnowflakeContracts: false,
  snowflakeTagsEnabled: false,
  enableComputePool: false,
  enableCreditsPage: false,
  enableOpportunityTicketCreation: false,
  enablePriorityTab: false,
  enableConnectionEdit: false,
  enableImplementedTab: false,
  enableSystemStatus: false,
  enableAdvancedGuardRails: false,
  enableReadOnlyOwnerRole: false,
  staleResourcesEnabled: false,
  exportLineageEnabled: false,
  enableBiFeature: false,
  enableKnowledgeEngine: false,
  enableKnowledgeEngineLearningsV2: false,
  enableKnowledgeEngineMultimodal: false,
  isDemoTenant: false,
  dbtIntegrationEnabled: false,
  enablePromptLibrary: false,
  enableWarehouseSizeRecommendation: false,
  enableCostAttributionInTeams: false,
  enableCloudIntegration: false,
  enableCustomWorkloadsChartAggregation: false,
  enableProjectionAgent: false,
  enableAIServicesInCodePage: false,
  queryAiServicesOwnerOnly: false,
  enableAIConversations: false,
  enableAICaching: false,
  isDatabricksTenant: false,
  // Which cloud THIS deployment runs on — selects the AWS or Azure variant of
  // the Databricks connection form. Defaults to "aws" backend-side.
  deploymentCloud: "aws",
  ssoGroupSyncEnabled: false,
  whitelistedDomains: false,
  enableAltimateCode: false,
  ssoUserRefreshMins: undefined,
  enableDbtJobsListing: false,
  enableStudioCitations: false,
  enableStudioShareableLinks: false,
  enableStudioScheduledReports: false,
  enableStudioWorkspaces: false,
  enableStudioOpportunities: false,
  disableTaskSelectionAutoTuneDatabricks: false,
  disableAllPurposeAutotune: false,
  showCloudCosts: false,
  // Cloud infrastructure cost in FUTURE STATE only. Current State keeps
  // rendering cloud under `showCloudCosts` regardless — projecting cloud
  // spend is a different question from reporting what has been billed.
  showCloudCostProjections: false,
  freemiumModeEnabled: false,
};

const appSlice = createSlice({
  name: "appState",
  initialState,
  reducers: {
    setHasAcceptedCookies: (state, action: PayloadAction<AppStateProps["hasAcceptedCookies"]>) => {
      state.hasAcceptedCookies = action.payload;
    },
    setPreviousPath: (state, action: PayloadAction<AppStateProps["previousPath"]>) => {
      state.previousPath = action.payload;
    },
    setAuthHeaderSet: (state, action: PayloadAction<AppStateProps["authHeaderSet"]>) => {
      state.authHeaderSet = action.payload;
    },
    setLoggedinUser: (state, action: PayloadAction<AppStateProps["loggedinUser"]>) => {
      state.loggedinUser = action.payload;
    },
    setSidebarExpanded: (state, action: PayloadAction<AppStateProps["sidebarExpanded"]>) => {
      state.sidebarExpanded = action.payload;
    },
    setHideSidebarExpandIcon: (
      state,
      action: PayloadAction<AppStateProps["hideSidebarExpandIcon"]>
    ) => {
      state.hideSidebarExpandIcon = action.payload;
    },
    setUserGroup: (state, action: PayloadAction<AppStateProps["userGroup"]>) => {
      state.userGroup = action.payload;
    },
    updateTenantInfo: (
      state,
      action: PayloadAction<Omit<GetTenantInfoResponse, "frontendUrl">>
    ) => {
      state.teammatesEnabled = action.payload.teammatesEnabled;
      state.currency = action.payload.currency || USD;
      state.currencySymbol = getCurrencySymbol(action.payload.currency || USD);
      state.rbacEnabled = action.payload.rbacEnabled || false;
      state.enableAutoTuneNotifications = action.payload.enableAutoTuneNotifications || false;
      state.exportLineageEnabled = action.payload.exportLineageEnabled || false;
      state.enableBiFeature = action.payload.enableBiFeature || false;
      state.dbtWorkloadEnabled = action.payload.dbtWorkloadEnabled || false;
      state.queryListingDefaultDaysCount = action.payload.queryListingDefaultDaysCount;
      state.jiraTicketEnabled = JIRA_TICKETING_ENABLED === "true";
      state.costAttributionEnabled = action.payload.costAttributionEnabled || false;
      state.accessRulesDisabled = action.payload.accessRulesDisabled || false;
      state.disableAccountCosts = action.payload.disableAccountCosts || false;
      state.costAnalysisReportsEnabled = action.payload.costAnalysisReportsEnabled || false;
      state.streamlitNotebooksEnabled = action.payload.streamlitNotebooksEnabled || false;
      state.enableStreamlitWorkloads = action.payload.enableStreamlitWorkloads || false;
      state.storedProceduresEnabled = action.payload.storedProceduresEnabled || false;
      state.sigmaWorkbooksEnabled = action.payload.sigmaWorkbooksEnabled || false;
      state.snowflakeTasksEnabled = action.payload.snowflakeTasksEnabled || false;
      state.enableCustomReports = action.payload.enableCustomReports || false;
      state.enableCostReports = action.payload.enableCostReports || false;
      state.enableTenantSwitching = action.payload.enableTenantSwitching || false;
      state.enableSnowflakeContracts = action.payload.enableSnowflakeContracts || false;
      state.snowflakeTagsEnabled = action.payload.snowflakeTagsEnabled || false;
      state.enableComputePool = action.payload.enableComputePool || false;
      state.enableCreditsPage = action.payload.enableCreditsPage || false;
      state.enableOpportunityTicketCreation =
        action.payload.enableOpportunityTicketCreation || false;
      state.enablePriorityTab = action.payload.enablePriorityTab || false;
      state.enableConnectionEdit = action.payload.enableConnectionEdit || false;
      state.enableImplementedTab = action.payload.enableImplementedTab || false;
      state.enableSystemStatus = action.payload.enableSystemStatus || false;
      state.enableAdvancedGuardRails = action.payload.enableAdvancedGuardRails || false;
      state.enableReadOnlyOwnerRole = action.payload.enableReadOnlyOwnerRole || false;
      state.staleResourcesEnabled = action.payload.staleResourcesEnabled || false;
      state.enableKnowledgeEngine = action.payload.enableKnowledgeEngine || false;
      state.enableKnowledgeEngineLearningsV2 =
        action.payload.enableKnowledgeEngineLearningsV2 || false;
      state.enableKnowledgeEngineMultimodal =
        action.payload.enableKnowledgeEngineMultimodal || false;
      state.isDemoTenant = action.payload.isDemoTenant || false;
      state.dbtIntegrationEnabled = action.payload.dbtIntegrationEnabled || false;
      state.enablePromptLibrary = action.payload.enablePromptLibrary || false;
      state.enableWarehouseSizeRecommendation =
        action.payload.enableWarehouseSizeRecommendation || false;
      state.enableCostAttributionInTeams = action.payload.enableCostAttributionInTeams || false;
      state.enableCloudIntegration = action.payload.enableCloudIntegration || false;
      state.enableCustomWorkloadsChartAggregation =
        action.payload.enableCustomWorkloadsChartAggregation ?? false;
      state.enableProjectionAgent = action.payload.enableProjectionAgent || false;
      state.enableAIServicesInCodePage = action.payload.enableAIServicesInCodePage || false;
      state.queryAiServicesOwnerOnly = action.payload.queryAiServicesOwnerOnly || false;
      state.enableAIConversations = action.payload.enableAIConversations || false;
      state.enableAICaching = action.payload.enableAICaching || false;
      state.isDatabricksTenant = action.payload.isDatabricksTenant || false;
      state.deploymentCloud = action.payload.deploymentCloud === "azure" ? "azure" : "aws";
      state.ssoGroupSyncEnabled = action.payload.ssoGroupSyncEnabled || false;
      state.whitelistedDomains = action.payload.whitelistedDomains || false;
      state.enableAltimateCode = action.payload.enableAltimateCode || false;
      state.ssoUserRefreshMins = action.payload.ssoUserRefreshMins ?? undefined;
      state.enableDbtJobsListing = action.payload.enableDbtJobsListing || false;
      state.enableStudioCitations = action.payload.enableStudioCitations || false;
      state.enableStudioShareableLinks = action.payload.enableStudioShareableLinks || false;
      state.enableStudioScheduledReports = action.payload.enableStudioScheduledReports || false;
      state.enableStudioWorkspaces = action.payload.enableStudioWorkspaces || false;
      state.enableStudioOpportunities = action.payload.enableStudioOpportunities || false;
      state.showDefaultScheduleBlocks = action.payload.showDefaultScheduleBlocks || false;
      state.disableTaskSelectionAutoTuneDatabricks =
        action.payload.disableTaskSelectionAutoTuneDatabricks || false;
      state.disableAllPurposeAutotune = action.payload.disableAllPurposeAutotune || false;
      state.showCloudCosts = action.payload.showCloudCosts || false;
      state.showCloudCostProjections = action.payload.showCloudCostProjections || false;
      state.freemiumModeEnabled = action.payload.freemiumModeEnabled || false;
    },
    setUserGroupsLoaded: (state, action: PayloadAction<AppStateProps["userGroupsLoaded"]>) => {
      state.userGroupsLoaded = action.payload;
    },
    updateDatastores: (state, action: PayloadAction<AppStateProps["datastores"]>) => {
      state.datastores = action.payload;
    },
    setCompanyInfo: (state, action: PayloadAction<AppStateProps["companyInfo"]>) => {
      state.companyInfo = action.payload;
    },
  },
});

export const {
  setLoggedinUser,
  setHasAcceptedCookies,
  setSidebarExpanded,
  setHideSidebarExpandIcon,
  setAuthHeaderSet,
  setPreviousPath,
  updateTenantInfo,
  updateDatastores,
  setUserGroup,
  setUserGroupsLoaded,
  setCompanyInfo,
} = appSlice.actions;
export default appSlice;
